/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import {
  BinaryTypeMetaArgument,
  BinaryTypeMetaInfoResponse,
  BinaryTypeMetaResponse,
  ExportBinaryTypeArgument,
  ExportBinaryTypeResponse,
  RemoveBinaryTypeArgument,
  RestoreBinaryTypeArgument,
} from './binary-meta';
import { CachePartitionsActionArgument, CachePartitionsActionResult } from './cache';
import {
  AddClusterUserActionArgument,
  ChangeTagActionArgument,
  ChangeTdeMasterKeyArgument,
  ClusterActionExportLogsResponse,
  ClusterCredentials,
  ListClusterUsersActionResponse,
  RemoveClusterUserActionArgument,
} from './cluster';
import {
  CloneAndDeployDeploymentUnitArgument,
  CloneDeploymentUnitArgument,
  ClusterActionMavenRepositoriesArgument,
  DeployDeploymentUnitArgument,
  DeploymentUnit,
  DeploymentUnitCloneActionResponse,
  DeploymentUnitCloneAndDeployActionResponse,
  DeploymentUnitCreateActionArgument,
  DeploymentUnitCreateActionResponse,
  DeploymentUnitRemoveActionArgument,
  DeploymentUnitRemoveVersionActionArgument,
  DeploymentUnitRenameActionArgument,
  DeploymentUnitUpdateArtifactsActionArgument,
  MavenRepositoryRequestData,
} from './code-deployment';
import { CacheName, PartitionId } from './common';
import {
  CancelComputeTaskSessionActionArgument,
  ChangeTaskPriorityActionArgument,
  ComputeTaskStatisticsActionArgument,
  ComputeTaskStatisticsByNode,
} from './compute';
import {
  ClusterActionCacheArgument,
  ClusterActionCacheConfigurationResult,
  ClusterActionCachesArgument,
  ClusterActionQueryResponseResult,
  ClusterActionQueryStatisticsResult,
  ClusterActionRunningQueryResult,
  ClusterActionScanExecuteArgument,
  ClusterActionSqlExecuteArgument,
  ClusterActionSqlNextPageArgument,
  RunningQueryConfigurationArgument,
  RunningQueryConfigurationResponse,
} from './queries';
import {
  QueryCancelActionArgument,
  QueryStatisticsActionArgument,
  RunningQueriesActionArgument,
  SnapshotActionCheckArgument,
  SnapshotActionCopyArgument,
  SnapshotActionCreateArgument,
  SnapshotActionDeleteArgument,
  SnapshotActionMoveArgument,
  SnapshotActionRestoreArgument,
  SnapshotScheduleCreateActionArgument,
  SnapshotScheduleDeleteActionArgument,
  SnapshotScheduleToggleActionArgument,
} from './snapshots';
import { TracingConfigurationActionArgument } from './tracing';

export type ActionId = string;

export enum ActionName {
  SQL_QUERY_EXECUTE = 'QueryActions.executeSqlQuery',
  SCAN_QUERY_EXECUTE = 'QueryActions.executeScanQuery',
  QUERY_CANCEL = 'QueryActions.cancel',
  QUERY_KILL = 'QueryActions.kill',
  QUERY_NEXT_PAGE = 'QueryActions.nextPage',
  CLUSTER_ACTIVATE = 'ClusterActions.activate',
  CLUSTER_DEACTIVATE = 'ClusterActions.deactivate',
  CLUSTER_EXPORT_LOGS = 'ClusterActions.exportLogs',
  BASELINE_SET_BASELINE_TOPOLOGY = 'BaselineActions.setBaselineTopology',
  BASELINE_UPDATE_AUTO_ADJUST_ENABLED = 'BaselineActions.updateAutoAdjustEnabled',
  BASELINE_UPDATE_AUTO_ADJUST_AWAIT_TIME = 'BaselineActions.updateAutoAdjustAwaitingTime',
  RUNNING_QUERIES = 'QueryActions.runningQueries',
  QUERY_STATISTICS = 'QueryActions.history',
  SNAPSHOT_RELOAD_LIST = 'SnapshotActions.reloadList',
  SNAPSHOT_CREATE = 'SnapshotActions.create',
  SNAPSHOT_DELETE = 'SnapshotActions.delete',
  SNAPSHOT_CHECK = 'SnapshotActions.check',
  SNAPSHOT_MOVE = 'SnapshotActions.move',
  SNAPSHOT_COPY = 'SnapshotActions.copy',
  SNAPSHOT_RESTORE = 'SnapshotActions.restore',
  SNAPSHOT_SCHEDULE_RELOAD_LIST = 'SnapshotScheduleActions.reloadList',
  SNAPSHOT_SCHEDULE_CREATE = 'SnapshotScheduleActions.create',
  SNAPSHOT_SCHEDULE_DELETE = 'SnapshotScheduleActions.delete',
  SNAPSHOT_SCHEDULE_ENABLE = 'SnapshotScheduleActions.enable',
  SNAPSHOT_SCHEDULE_DISABLE = 'SnapshotScheduleActions.disable',
  CHANGE_TRACING_CONFIGURATION = 'TracingActions.changeConfiguration',
  CACHE_PARTITIONS = 'CacheActions.partitions',
  LOST_PARTITIONS = 'CacheActions.lostPartitions',
  RESET_LOST_PARTITIONS = 'CacheActions.resetLostPartitions',
  CACHE_CONFIGURATION = 'CacheActions.configuration',
  CACHE_CLEAR = 'CacheActions.clear',
  CACHE_DESTROY = 'CacheActions.destroy',
  CACHE_REBALANCE = 'CacheActions.rebalance',
  CACHE_ENABLE_STATISTICS = 'CacheActions.enableStatistics',
  CACHE_DISABLE_STATISTICS = 'CacheActions.disableStatistics',
  CACHE_LOAD_FROM_STORE = 'CacheActions.loadCache',
  NODE_CONFIGURATION = 'NodeActions.configuration',
  DEPLOYMENT_UNITS = 'CodeDeploymentActions.list',
  DEPLOYMENT_UNITS_CREATE = 'CodeDeploymentActions.create',
  DEPLOYMENT_UNITS_RENAME = 'CodeDeploymentActions.rename',
  DEPLOYMENT_UNITS_REMOVE = 'CodeDeploymentActions.remove',
  DEPLOYMENT_UNITS_DEPLOY = 'CodeDeploymentActions.deploy',
  DEPLOYMENT_UNITS_VERSION_UPDATE_ARTIFACTS = 'CodeDeploymentActions.updateArtifacts',
  DEPLOYMENT_UNITS_VERSION_REMOVE = 'CodeDeploymentActions.removeVersion',
  DEPLOYMENT_UNITS_CLONE = 'CodeDeploymentActions.clone',
  DEPLOYMENT_UNITS_CLONE_AND_DEPLOY = 'CodeDeploymentActions.cloneAndDeploy',
  MAVEN_REPOSITORIES = 'CodeDeploymentActions.listMavenRepositories',
  MAVEN_REPOSITORIES_UPDATE = 'CodeDeploymentActions.mavenRepositories',
  CHANGE_TAG = 'ClusterActions.changeTag',
  LIST_CLUSTER_USERS = 'NebulaUserActions.list',
  ADD_CLUSTER_USER = 'NebulaUserActions.add',
  REMOVE_CLUSTER_USER = 'NebulaUserActions.remove',
  CANCEL_COMPUTE_TASK_SESSION = 'ComputeActions.cancel',
  COMPUTE_TASK_STATISTICS = 'ComputeActions.taskStatistics',
  CHANGE_TASK_PRIORITY = 'ComputeActions.changePriority',
  CHANGE_TDE_MASTER_KEY = 'TdeActions.changeMasterKey',
  TRIGGER_NODE_GC = 'NodeActions.gc',
  GET_THREAD_DUMP = 'NodeActions.threadDump',
  BINARY_TYPES_LIST = 'BinaryTypeActions.metaAll',
  BINARY_TYPE_DETAILS = 'BinaryTypeActions.meta',
  BINARY_TYPE_EXPORT = 'BinaryTypeActions.export',
  BINARY_TYPE_REMOVE = 'BinaryTypeActions.remove',
  BINARY_TYPE_RESTORE = 'BinaryTypeActions.update',
  PAUSE_CACHE_DATA_REPLICATION = 'CacheDrActions.pauseIncrementalDataReplication',
  RESUME_CACHE_DATA_REPLICATION = 'CacheDrActions.resumeIncrementalDataReplication',
  START_CACHE_FST = 'CacheDrActions.startFullStateTransfer',
  STOP_CACHE_FST = 'CacheDrActions.stopFullStateTransfer',
  UPDATE_LICENSE = 'LicenseActions.uploadLicense',
  UPDATE_RUNNING_QUERY_CONFIGURATION = 'QueryActions.updateRunningQueryConfiguration',
  GET_RUNNING_QUERY_CONFIGURATION = 'QueryActions.getRunningQueryConfiguration',
  SERVICES_STOP = 'ServiceActions.stop',
}

// List of distributed actions as union type (A|B|...|Z)
export type DistributedActions =
  | ActionName.RUNNING_QUERIES
  | ActionName.CACHE_PARTITIONS
  | ActionName.NODE_CONFIGURATION
  | ActionName.CANCEL_COMPUTE_TASK_SESSION
  | ActionName.CHANGE_TASK_PRIORITY
  | ActionName.TRIGGER_NODE_GC
  | ActionName.GET_THREAD_DUMP
  | ActionName.UPDATE_LICENSE;
// List of actions that can be both distributed and usual actions as union type (A|B|...|Z)
export type DualActions = ActionName.SQL_QUERY_EXECUTE;

export enum ResponseStatus {
  COMPLETED = 'COMPLETED',
  RUNNING = 'RUNNING',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

export interface ClusterAction<T extends ActionName> {
  id: ActionId;
  action: ActionName;
  nodeIds?: string[];
  argument: ActionArgument[T];
  timestamp: number;
  credentials?: ClusterCredentials;
  // Secure cluster to backend session ID
  sessionId?: string;
}

interface BaseClusterActionResponse {
  id: string;
  nodeConsistentId: string;
  requestId: string; // taskId and actionId
  timestamp: number;
}

export interface ClusterActionRunningResponse extends BaseClusterActionResponse {
  status: ResponseStatus.RUNNING;
}

export interface ClusterActionCompletedResponse<T extends ActionName> extends BaseClusterActionResponse {
  result: ActionResponse[T];
  status: ResponseStatus.COMPLETED;
}

export interface ClusterActionResponseError {
  code: number;
  message: string;
  stackTrace: any[];
}

export interface ClusterActionFailedResponse extends BaseClusterActionResponse {
  error: ClusterActionResponseError;
  status: ResponseStatus.FAILED;
}

export type ErrorJob = ClusterActionFailedResponse & { status: ResponseStatus.FAILED };

export type ClusterActionResponse<T extends ActionName> =
  | ClusterActionRunningResponse
  | ClusterActionCompletedResponse<T>
  | ClusterActionFailedResponse;

export interface TaskResponse {
  id: string;
  nodeConsistentId: string | null;
  status: ResponseStatus;
  jobCount: number;
}

export interface ActionResponse {
  [ActionName.QUERY_NEXT_PAGE]: ClusterActionQueryResponseResult;
  [ActionName.SCAN_QUERY_EXECUTE]: ClusterActionQueryResponseResult[];
  [ActionName.SQL_QUERY_EXECUTE]: ClusterActionQueryResponseResult[];
  [ActionName.QUERY_CANCEL]: null;
  [ActionName.QUERY_KILL]: null;
  [ActionName.CLUSTER_ACTIVATE]: null;
  [ActionName.CLUSTER_DEACTIVATE]: null;
  [ActionName.CLUSTER_EXPORT_LOGS]: ClusterActionExportLogsResponse;
  [ActionName.BASELINE_SET_BASELINE_TOPOLOGY]: null;
  [ActionName.BASELINE_UPDATE_AUTO_ADJUST_ENABLED]: null;
  [ActionName.BASELINE_UPDATE_AUTO_ADJUST_AWAIT_TIME]: null;
  [ActionName.RUNNING_QUERIES]: ClusterActionRunningQueryResult[];
  [ActionName.QUERY_STATISTICS]: ClusterActionQueryStatisticsResult[];
  [ActionName.SNAPSHOT_RELOAD_LIST]: null;
  [ActionName.SNAPSHOT_CREATE]: null;
  [ActionName.SNAPSHOT_DELETE]: null;
  [ActionName.SNAPSHOT_CHECK]: null;
  [ActionName.SNAPSHOT_MOVE]: null;
  [ActionName.SNAPSHOT_COPY]: null;
  [ActionName.SNAPSHOT_RESTORE]: null;
  [ActionName.SNAPSHOT_SCHEDULE_RELOAD_LIST]: null;
  [ActionName.SNAPSHOT_SCHEDULE_CREATE]: null;
  [ActionName.SNAPSHOT_SCHEDULE_DELETE]: null;
  [ActionName.SNAPSHOT_SCHEDULE_ENABLE]: null;
  [ActionName.SNAPSHOT_SCHEDULE_DISABLE]: null;
  [ActionName.CHANGE_TRACING_CONFIGURATION]: null;
  [ActionName.LOST_PARTITIONS]: LostPartitionsResult;
  [ActionName.RESET_LOST_PARTITIONS]: null;
  [ActionName.CACHE_PARTITIONS]: CachePartitionsActionResult;
  [ActionName.CACHE_CONFIGURATION]: ClusterActionCacheConfigurationResult;
  [ActionName.CACHE_CLEAR]: null;
  [ActionName.CACHE_DESTROY]: null;
  [ActionName.CACHE_REBALANCE]: null;
  [ActionName.CACHE_ENABLE_STATISTICS]: null;
  [ActionName.CACHE_DISABLE_STATISTICS]: null;
  [ActionName.CACHE_LOAD_FROM_STORE]: null;
  [ActionName.NODE_CONFIGURATION]: string;
  [ActionName.DEPLOYMENT_UNITS]: DeploymentUnit[];
  [ActionName.DEPLOYMENT_UNITS_CREATE]: DeploymentUnitCreateActionResponse;
  [ActionName.DEPLOYMENT_UNITS_RENAME]: null;
  [ActionName.DEPLOYMENT_UNITS_REMOVE]: null;
  [ActionName.DEPLOYMENT_UNITS_DEPLOY]: null;
  [ActionName.DEPLOYMENT_UNITS_VERSION_UPDATE_ARTIFACTS]: null;
  [ActionName.DEPLOYMENT_UNITS_VERSION_REMOVE]: null;
  [ActionName.DEPLOYMENT_UNITS_CLONE]: DeploymentUnitCloneActionResponse;
  [ActionName.DEPLOYMENT_UNITS_CLONE_AND_DEPLOY]: DeploymentUnitCloneAndDeployActionResponse;
  [ActionName.MAVEN_REPOSITORIES]: MavenRepositoryRequestData[];
  [ActionName.MAVEN_REPOSITORIES_UPDATE]: null;
  [ActionName.CHANGE_TAG]: null;
  [ActionName.LIST_CLUSTER_USERS]: ListClusterUsersActionResponse;
  [ActionName.ADD_CLUSTER_USER]: null;
  [ActionName.REMOVE_CLUSTER_USER]: null;
  [ActionName.CANCEL_COMPUTE_TASK_SESSION]: null;
  [ActionName.COMPUTE_TASK_STATISTICS]: ComputeTaskStatisticsByNode[];
  [ActionName.CHANGE_TASK_PRIORITY]: null;
  [ActionName.CHANGE_TDE_MASTER_KEY]: null;
  [ActionName.TRIGGER_NODE_GC]: null;
  [ActionName.GET_THREAD_DUMP]: ThreadDump;
  [ActionName.BINARY_TYPES_LIST]: BinaryTypeMetaInfoResponse;
  [ActionName.BINARY_TYPE_DETAILS]: BinaryTypeMetaResponse;
  [ActionName.BINARY_TYPE_EXPORT]: ExportBinaryTypeResponse;
  [ActionName.BINARY_TYPE_REMOVE]: null;
  [ActionName.BINARY_TYPE_RESTORE]: null;
  [ActionName.PAUSE_CACHE_DATA_REPLICATION]: null;
  [ActionName.RESUME_CACHE_DATA_REPLICATION]: null;
  [ActionName.START_CACHE_FST]: null;
  [ActionName.STOP_CACHE_FST]: null;
  [ActionName.UPDATE_LICENSE]: null;
  [ActionName.UPDATE_RUNNING_QUERY_CONFIGURATION]: null;
  [ActionName.GET_RUNNING_QUERY_CONFIGURATION]: RunningQueryConfigurationResponse;
  [ActionName.SERVICES_STOP]: null;
}

export interface ActionArgument {
  [ActionName.QUERY_NEXT_PAGE]: ClusterActionSqlNextPageArgument;
  [ActionName.SCAN_QUERY_EXECUTE]: ClusterActionScanExecuteArgument;
  [ActionName.SQL_QUERY_EXECUTE]: ClusterActionSqlExecuteArgument;
  [ActionName.QUERY_CANCEL]: QueryCancelActionArgument;
  [ActionName.QUERY_KILL]: QueryCancelActionArgument;
  [ActionName.CLUSTER_ACTIVATE]: null;
  [ActionName.CLUSTER_DEACTIVATE]: null;
  [ActionName.CLUSTER_EXPORT_LOGS]: number;
  [ActionName.BASELINE_SET_BASELINE_TOPOLOGY]: BaselineSetBaselineTopologyArgument;
  [ActionName.BASELINE_UPDATE_AUTO_ADJUST_ENABLED]: BaselineUpdateAutoAdjustEnabledArgument;
  [ActionName.BASELINE_UPDATE_AUTO_ADJUST_AWAIT_TIME]: BaselineUpdateAutoAdjustAwaitTimeArgument;
  [ActionName.RUNNING_QUERIES]: RunningQueriesActionArgument;
  [ActionName.QUERY_STATISTICS]: QueryStatisticsActionArgument;
  [ActionName.SNAPSHOT_RELOAD_LIST]: null;
  [ActionName.SNAPSHOT_CREATE]: SnapshotActionCreateArgument;
  [ActionName.SNAPSHOT_DELETE]: SnapshotActionDeleteArgument;
  [ActionName.SNAPSHOT_CHECK]: SnapshotActionCheckArgument;
  [ActionName.SNAPSHOT_MOVE]: SnapshotActionMoveArgument;
  [ActionName.SNAPSHOT_COPY]: SnapshotActionCopyArgument;
  [ActionName.SNAPSHOT_RESTORE]: SnapshotActionRestoreArgument;
  [ActionName.SNAPSHOT_SCHEDULE_RELOAD_LIST]: null;
  [ActionName.SNAPSHOT_SCHEDULE_CREATE]: SnapshotScheduleCreateActionArgument;
  [ActionName.SNAPSHOT_SCHEDULE_DELETE]: SnapshotScheduleDeleteActionArgument;
  [ActionName.SNAPSHOT_SCHEDULE_ENABLE]: SnapshotScheduleToggleActionArgument;
  [ActionName.SNAPSHOT_SCHEDULE_DISABLE]: SnapshotScheduleToggleActionArgument;
  [ActionName.CHANGE_TRACING_CONFIGURATION]: TracingConfigurationActionArgument;
  [ActionName.LOST_PARTITIONS]: LostPartitionsArgument;
  [ActionName.RESET_LOST_PARTITIONS]: LostPartitionsArgument;
  [ActionName.CACHE_PARTITIONS]: CachePartitionsActionArgument;
  [ActionName.CACHE_CONFIGURATION]: ClusterActionCacheArgument;
  [ActionName.CACHE_CLEAR]: ClusterActionCacheArgument;
  [ActionName.CACHE_DESTROY]: ClusterActionCacheArgument;
  [ActionName.CACHE_REBALANCE]: ClusterActionCacheArgument;
  [ActionName.CACHE_ENABLE_STATISTICS]: ClusterActionCachesArgument;
  [ActionName.CACHE_DISABLE_STATISTICS]: ClusterActionCachesArgument;
  [ActionName.CACHE_LOAD_FROM_STORE]: ClusterActionCacheArgument;
  [ActionName.NODE_CONFIGURATION]: null;
  [ActionName.DEPLOYMENT_UNITS]: null;
  [ActionName.DEPLOYMENT_UNITS_CREATE]: DeploymentUnitCreateActionArgument;
  [ActionName.DEPLOYMENT_UNITS_RENAME]: DeploymentUnitRenameActionArgument;
  [ActionName.DEPLOYMENT_UNITS_REMOVE]: DeploymentUnitRemoveActionArgument;
  [ActionName.DEPLOYMENT_UNITS_DEPLOY]: DeployDeploymentUnitArgument;
  [ActionName.DEPLOYMENT_UNITS_VERSION_UPDATE_ARTIFACTS]: DeploymentUnitUpdateArtifactsActionArgument;
  [ActionName.DEPLOYMENT_UNITS_VERSION_REMOVE]: DeploymentUnitRemoveVersionActionArgument;
  [ActionName.DEPLOYMENT_UNITS_CLONE]: CloneDeploymentUnitArgument;
  [ActionName.DEPLOYMENT_UNITS_CLONE_AND_DEPLOY]: CloneAndDeployDeploymentUnitArgument;
  [ActionName.MAVEN_REPOSITORIES]: null;
  [ActionName.MAVEN_REPOSITORIES_UPDATE]: ClusterActionMavenRepositoriesArgument;
  [ActionName.CHANGE_TAG]: ChangeTagActionArgument;
  [ActionName.LIST_CLUSTER_USERS]: null;
  [ActionName.ADD_CLUSTER_USER]: AddClusterUserActionArgument;
  [ActionName.REMOVE_CLUSTER_USER]: RemoveClusterUserActionArgument;
  [ActionName.CANCEL_COMPUTE_TASK_SESSION]: CancelComputeTaskSessionActionArgument;
  [ActionName.COMPUTE_TASK_STATISTICS]: ComputeTaskStatisticsActionArgument;
  [ActionName.CHANGE_TASK_PRIORITY]: ChangeTaskPriorityActionArgument;
  [ActionName.CHANGE_TDE_MASTER_KEY]: ChangeTdeMasterKeyArgument;
  [ActionName.TRIGGER_NODE_GC]: null;
  [ActionName.GET_THREAD_DUMP]: null;
  [ActionName.BINARY_TYPES_LIST]: null;
  [ActionName.BINARY_TYPE_DETAILS]: BinaryTypeMetaArgument;
  [ActionName.BINARY_TYPE_EXPORT]: ExportBinaryTypeArgument;
  [ActionName.BINARY_TYPE_REMOVE]: RemoveBinaryTypeArgument;
  [ActionName.BINARY_TYPE_RESTORE]: RestoreBinaryTypeArgument;
  [ActionName.PAUSE_CACHE_DATA_REPLICATION]: DrCacheActionArgument;
  [ActionName.RESUME_CACHE_DATA_REPLICATION]: DrCacheActionArgument;
  [ActionName.START_CACHE_FST]: DrCacheActionArgument;
  [ActionName.STOP_CACHE_FST]: DrCacheActionArgument;
  [ActionName.UPDATE_LICENSE]: UpdateClusterLicenseActionArgument;
  [ActionName.UPDATE_RUNNING_QUERY_CONFIGURATION]: RunningQueryConfigurationArgument;
  [ActionName.GET_RUNNING_QUERY_CONFIGURATION]: null;
  [ActionName.SERVICES_STOP]: StopServicesActionArgument;
}

type BaselineSetBaselineTopologyArgument = { consistentIds: string[] };
type BaselineUpdateAutoAdjustEnabledArgument = { autoAdjustEnabled: boolean };
type BaselineUpdateAutoAdjustAwaitTimeArgument = { awaitingTime: number };
interface LostPartitionsResult {
  lostPartitions: Record<CacheName, PartitionId[]>;
}
interface LostPartitionsArgument {
  cacheNames: CacheName[];
}

export enum ThreadState {
  /**
   * A thread that has not yet started is in this state
   */
  NEW = 'NEW',
  /**
   * A thread executing in the Java virtual machine is in this state.
   */
  RUNNABLE = 'RUNNABLE',
  /**
   * A thread that is blocked waiting for a monitor lock is in this state
   */
  BLOCKED = 'BLOCKED',
  /**
   * A thread that is waiting indefinitely for another thread to perform a particular action is in this state
   */
  WAITING = 'WAITING',
  /**
   * A thread that is waiting for another thread to perform an action for up to a specified waiting time is in this state.
   */
  TIMED_WAITING = 'TIMED_WAITING',
  /**
   * A thread that has existed is in this state.
   */
  TERMINATED = 'TERMINATED',
}

export type Thread = { name: string; id: number; state: ThreadState; stackTrace: string };
export type ThreadDump = Thread[];

export type DrCacheActionArgument = { cacheNames: CacheName[]; dataCenterIds?: number[] };

export type ClusterLicenseText = string;
export type UpdateClusterLicenseActionArgument = { license: ClusterLicenseText };

export type ServiceName2x = string;
export type StopServicesActionArgument = ServiceName2x[];
